import {
  mdiArrowDownDropCircleOutline,
  mdiArrowRightDropCircleOutline,
  mdiDeleteOutline,
  mdiFileSearchOutline,
  mdiPencil
} from "@mdi/js";
import Icon from "@mdi/react";
import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Grid,
  PaginationProps,
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
  Table
} from "semantic-ui-react";

import { CommonTable } from "../../../components";
import PaginationView from "../../../components/pagination/Pagination";
import { SimulationNetZeroDataTable } from "../../../config/constants";
import { warningMessage } from "../../../helpers/ErrorHandler";
import "./SimulationTable.scss";

interface SimulationTableProps {
  data: DrawingSimulationData[] | undefined;
  onEdit: (data: DrawingSimulationData, isView?: boolean) => void;
  onDelete: (data: DrawingSimulationData) => void;
  setPageNumber: (pageNumber: number) => void;
  setPageSize: (pageSize: number) => void;
  pageNumber: number;
  pageSize: number;
  totalSimulations: number;
  isGetProductSimulationsLoading: boolean;
  currentDrawingId: (id: string) => void;
}

type ClimateImpactData = {
  processGates?: any[];
  farmGates?: any[];
  externalStorages?: { storage?: any[] };
  externalTransports?: { transport?: any[] };
};

const LoadingComponent = () => (
  <Placeholder fluid>
    <PlaceholderParagraph>
      <PlaceholderLine />
      <PlaceholderLine />
      <PlaceholderLine />
      <PlaceholderLine />
      <PlaceholderLine />
    </PlaceholderParagraph>
    <PlaceholderParagraph>
      <PlaceholderLine />
      <PlaceholderLine />
      <PlaceholderLine />
    </PlaceholderParagraph>
  </Placeholder>
);

const SimulationTable: React.FC<SimulationTableProps> = ({
  data,
  onEdit,
  onDelete,
  setPageNumber,
  setPageSize,
  pageNumber,
  pageSize,
  totalSimulations,
  isGetProductSimulationsLoading,
  currentDrawingId
}) => {
  const [totalPages, setTotalPages] = useState(10);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [selectNetZeroId, setSelectNetZeroId] = useState<any>(null);

  useEffect(() => {
    setTotalPages(Math.ceil(totalSimulations / pageSize));
  }, [totalSimulations, pageSize]);

  const handlePaginationChange = (e: any, { activePage, totalPages }: PaginationProps) => {
    if (typeof activePage === "number") setPageNumber(activePage);
    if (typeof totalPages === "number") setTotalPages(totalPages);
  };

  const getCurrentImpact = (originalImpact: number, simulatedImpact: number) => {
    return simulatedImpact
      ? Math.fround(((simulatedImpact - originalImpact) / originalImpact) * 100)
      : 0;
  };

  const scaleFactor = (
    filterOtherData: any[],
    simulationOriginalImpact: number,
    originalImpact: number
  ) => {
    const percentageChange = filterOtherData.reduce((acc, curr) => {
      return acc + getCurrentImpact(originalImpact, curr.totalImpact?.climateChange?.measure || 0);
    }, 0);

    const scalingFactorValue =
      Number(getCurrentImpact(originalImpact, simulationOriginalImpact)) / percentageChange;
    return scalingFactorValue;
  };

  const handleRowClick = (rowId: number) => {
    const currentExpandedRows = expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

    const newExpandedRows = isRowCurrentlyExpanded
      ? currentExpandedRows.filter(id => id !== rowId)
      : currentExpandedRows.concat(rowId);
    setExpandedRows(newExpandedRows);
  };

  const checkNetZero = (item: any, data: any) => {
    if (item._id === selectNetZeroId) return;
    setSelectNetZeroId(item._id);
    currentDrawingId(item._id);
  };

  const renderItemCaret = (rowId: number) => {
    const currentExpandedRows = expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

    if (isRowCurrentlyExpanded) {
      return mdiArrowDownDropCircleOutline;
    } else {
      return mdiArrowRightDropCircleOutline;
    }
  };

  const renderItem = (item: DrawingSimulationData, index: number) => {
    const simulationProductSummaryData: any = item?.simulationProductSummary || [];

    const originalImpact = item?.productSummary?.totalImpact;
    const filterOriginalData = item.originalSimulationId
      ? simulationProductSummaryData?.find((e: any) => e._id === item.originalSimulationId)
      : simulationProductSummaryData[0] || {};
    const filterOtherData = simulationProductSummaryData?.filter(
      (e: any) => e._id !== item.originalSimulationId
    );

    const simulationOriginalImpact =
      item.originalSimulationId === null && simulationProductSummaryData.length > 0
        ? { totalImpact: item?.productSummary?.totalImpact }
        : filterOriginalData;

    const scaleFactorValue = scaleFactor(
      filterOtherData,
      simulationOriginalImpact?.totalImpact?.climateChange?.measure || 0,
      originalImpact || 0
    );

    // Calculate original and simulation costs
    const getTotalCost = (totalConsumption: any) => {
      if (!totalConsumption) return 0;
      return (
        Number(totalConsumption.totalElectricityConsumptionCost || 0) +
        Number(totalConsumption.totalHeatConsumptionCost || 0) +
        Number(totalConsumption.totalWaterConsumptionCost || 0)
      );
    };

    const originalCost = getTotalCost(item?.productSummary?.totalConsumption);
    const simulationCost = getTotalCost(simulationOriginalImpact?.totalConsumption);

    const itemRows: any = [
      <Table.Row key={`row-data-${index}`}>
        <Table.Cell
          onClick={() => {
            filterOtherData.length > 0
              ? handleRowClick(index)
              : warningMessage("Simulation Data not available");
          }}
        >
          <Icon
            className="table-icon"
            color="var(--tableEditIcon)"
            path={renderItemCaret(index)}
            size={1.2}
          />
        </Table.Cell>
        <Table.Cell>{item?.drawingName}</Table.Cell>
        <Table.Cell>{originalImpact?.toFixed(5)}</Table.Cell>
        <Table.Cell>
          {simulationOriginalImpact?.totalImpact?.climateChange?.measure.toFixed(5) ||
            simulationOriginalImpact?.totalImpact?.toFixed(5)}
        </Table.Cell>
        <Table.Cell>{originalCost.toFixed(2)}</Table.Cell>
        <Table.Cell>{simulationCost.toFixed(2)}</Table.Cell>
        <Table.Cell>
          {getCurrentImpact(
            originalImpact || 0,
            simulationOriginalImpact?.totalImpact?.climateChange?.measure || 0
          ).toFixed(4)}
        </Table.Cell>
        <Table.Cell>
          <Grid columns={3}>
            <Grid.Column>
              <div
                onClick={() => {
                  onEdit(item, true);
                }}
              >
                <Icon
                  className="table-icon"
                  color="var(--tableEditIcon)"
                  path={mdiFileSearchOutline}
                  size={1.2}
                />
              </div>
            </Grid.Column>
            <Grid.Column>
              <div
                onClick={() => {
                  onEdit(item);
                }}
              >
                <Icon
                  className="table-icon"
                  color="var(--tableEditIcon)"
                  path={mdiPencil}
                  size={1.2}
                />
              </div>
            </Grid.Column>
            <Grid.Column>
              <div
                onClick={() => {
                  onDelete(item);
                }}
              >
                <Icon
                  className="table-icon"
                  color="var(--tableEditIcon)"
                  path={mdiDeleteOutline}
                  size={1.2}
                />
              </div>
            </Grid.Column>
          </Grid>
        </Table.Cell>
        <Table.Cell className="netZeroContentCheckbox">
          <Checkbox
            checked={item._id === selectNetZeroId}
            onChange={(e, data) => checkNetZero(item, data)}
          />
        </Table.Cell>
      </Table.Row>
    ];

    if (expandedRows.includes(index)) {
      filterOtherData.map((data: any) => {
        const expandedSimulationCost = getTotalCost(data?.totalConsumption);
        return itemRows.push(
          <Table.Row key={"row-expanded-" + index}>
            <Table.Cell></Table.Cell>
            <Table.Cell>{data?.name}</Table.Cell>
            <Table.Cell>{originalImpact?.toFixed(5)}</Table.Cell>
            <Table.Cell>{data?.totalImpact?.climateChange?.measure.toFixed(5)}</Table.Cell>
            <Table.Cell>{originalCost.toFixed(2)}</Table.Cell>
            <Table.Cell>{expandedSimulationCost.toFixed(2)}</Table.Cell>
            <Table.Cell>
              {(
                Number(
                  getCurrentImpact(
                    originalImpact || 0,
                    data?.totalImpact?.climateChange?.measure || 0
                  )
                ) * scaleFactorValue
              ).toFixed(4)}
            </Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
          </Table.Row>
        );
      });
    }

    return itemRows;
  };

  return (
    <Grid>
      <Grid.Column computer={16}>
        <CommonTable tableHeaderData={SimulationNetZeroDataTable}>
          {isGetProductSimulationsLoading && (
            <Table.Row>
              <Table.Cell colSpan="7">
                <LoadingComponent />
              </Table.Cell>
            </Table.Row>
          )}
          {!isGetProductSimulationsLoading &&
            data?.map((item: DrawingSimulationData, index) => renderItem(item, index))}
        </CommonTable>
      </Grid.Column>
      <Grid.Column computer={16}>
        <PaginationView
          changePageSize={setPageSize}
          pageSize={pageSize}
          totalPages={totalPages}
          currentPageNo={pageNumber}
          currentSelectPage={handlePaginationChange}
        />
      </Grid.Column>
    </Grid>
  );
};

export default SimulationTable;
